import React, { useState, useId } from 'react';
import { ChevronDownIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
type ResearchStatus = 'verified' | 'illustrative' | 'pending';
type ResearchSourceProps = {
  sourceId: string;
  status: ResearchStatus;
  title: string;
  summary: string;
  methodology: string;
  sourceUrl?: string;
  sourceLabel?: string;
  published?: string;
  className?: string;
};
const statusCopy: Record<ResearchStatus, string> = {
  verified: 'VERIFIED PUBLIC RESEARCH',
  illustrative: 'ILLUSTRATIVE PROTOTYPE',
  pending: 'SOURCE REQUIRED / PENDING VALIDATION'
};
const statusTone: Record<ResearchStatus, string> = {
  verified: 'border-[#72c7e8]/50 bg-[#062c50] text-white',
  illustrative: 'border-[#ff4d17]/40 bg-[#fff4ef] text-[#11110f]',
  pending: 'border-black/20 bg-[#f7f5ef] text-[#11110f]'
};
export function ResearchSource({
  sourceId,
  status,
  title,
  summary,
  methodology,
  sourceUrl,
  sourceLabel = 'OPEN SOURCE',
  published,
  className = ''
}: ResearchSourceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const contentId = useId();
  return <aside className={`border ${statusTone[status]} ${className}`} aria-label={`Evidence record ${sourceId}`} data-id="element-781" data-mp="1reuagzmiuw5b">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-782" data-mp="1wbukflmhsj1p">
        <div className="min-w-0" data-id="element-783" data-mp="1icctazlq4tp6">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold tracking-[0.12em]" data-id="element-784" data-mp="1ifh3qeps8loa">
            <span className="border border-current/30 px-2 py-1" data-id="element-785" data-mp="1na4i3kr2nzp5">
              {sourceId}
            </span>
            <span className="opacity-75" data-id="element-786" data-mp="1deb9gcgh4qcn">{statusCopy[status]}</span>
          </div>
          <p className="mt-3 text-sm font-extrabold" data-id="element-787" data-mp="1dgdyou9syfgb">{title}</p>
        </div>
        <button type="button" aria-expanded={isOpen} aria-controls={contentId} onClick={() => setIsOpen(current => !current)} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 self-start border border-current/30 px-3 text-xs font-extrabold transition-opacity hover:opacity-70 sm:self-auto" data-id="element-788" data-mp="32tr3ekltu5hy">
          SOURCE DETAILS
          <motion.span animate={reduceMotion ? undefined : {
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-789" data-mp="2wcz9ysuicmow">
            <ChevronDownIcon size={15} aria-hidden="true" data-id="element-790" data-mp="3p1n2n9hl6ech" />
          </motion.span>
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-791" data-mp="3k2trhnpruzfh">
        {isOpen && <motion.div id={contentId} initial={reduceMotion ? false : {
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: [0.22, 1, 0.36, 1]
      }} className="border-t border-current/25 px-4 py-4" data-id="element-792" data-mp="3k33dz1blyogf">
            <div className="grid gap-4 md:grid-cols-[0.85fr_1.15fr]" data-id="element-793" data-mp="6uyxxkqg7t7c">
              <div data-id="element-794" data-mp="1pj1n9h6b7dht">
                <p className="text-[10px] font-extrabold tracking-[0.12em] opacity-70" data-id="element-795" data-mp="1pjtgwmgcj15g">
                  WHAT THIS RECORD MEANS
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90" data-id="element-796" data-mp="1ujplonwvawm2">
                  {summary}
                </p>
              </div>
              <div data-id="element-797" data-mp="23it5ju3uzg1m">
                <p className="text-[10px] font-extrabold tracking-[0.12em] opacity-70" data-id="element-798" data-mp="23jccygvvum1e">
                  METHOD / PROVENANCE
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90" data-id="element-799" data-mp="28ft8hw5ew2oi">
                  {methodology}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-current/25 pt-4 text-xs font-bold" data-id="element-800" data-mp="2i7z2fbf84u6c">
              {published && <span data-id="element-801" data-mp="2mavcueum1jz7">PUBLISHED {published}</span>}
              {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 underline decoration-current/40 underline-offset-4 hover:decoration-current" data-id="element-802" data-mp="2r9giitsmsvv1">
                  <ExternalLinkIcon size={14} aria-hidden="true" data-id="element-803" data-mp="2ij6rot26b6y5" />
                  {sourceLabel}
                </a> : <span className="inline-flex items-center gap-1.5 opacity-75" data-id="element-804" data-mp="2dkn4kpbufp47">
                  <FileTextIcon size={14} aria-hidden="true" data-id="element-805" data-mp="2sakm4mf9pgkf" />
                  NO APPROVED EXTERNAL SOURCE ATTACHED
                </span>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </aside>;
}